import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { STAGES, stageChipClass, formatDate } from "@/lib/constants";
import { Search } from "lucide-react";

export default function ClientsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const stage = params.get("stage") || "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", {
        params: { journey_stage: stage || undefined, q: q || undefined },
      });
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1 text-muted-foreground">The book</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">Every relationship, at every stage of the journey.</p>
        </div>
        <form className="relative" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-72 pl-9"
            placeholder="Search name, email, mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="clients-search-input"
          />
        </form>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setParams({})}
          data-testid="filter-all"
          className={
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-200 " +
            (!stage ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-foreground hover:bg-secondary")
          }
        >
          All
        </button>
        {STAGES.map((s, i) => (
          <button
            key={s}
            onClick={() => setParams({ stage: s })}
            data-testid={`filter-stage-${i + 1}`}
            className={
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-200 " +
              (stage === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-foreground hover:bg-secondary")
            }
          >
            {s}
          </button>
        ))}
      </div>

      <Card className="border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Since</th>
              </tr>
            </thead>
            <tbody data-testid="clients-table-body">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-muted-foreground">No clients found for this filter.</td></tr>
              ) : clients.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/app/clients/${c.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-secondary/30"
                  data-testid={`client-row-${c.id}`}
                >
                  <td className="px-4 py-4 font-medium">{c.full_name}</td>
                  <td className="px-4 py-4 text-muted-foreground">
                    <div>{c.email}</div>
                    <div className="text-xs">{c.mobile}</div>
                  </td>
                  <td className="px-4 py-4"><span className={stageChipClass(c.journey_stage)}>{c.journey_stage}</span></td>
                  <td className="px-4 py-4 text-xs text-muted-foreground">{formatDate(c.conversion_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
